import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Image from 'components/AppImage';
import statusOptions from 'utils/BookingStatus';

const BookingsTable = ({ 
  bookingList, 
  onEdit, 
  onViewDetails, 
  onCancelBooking, 
  selectedBooking, 
  onSelectBooking,
  onSelectAll,
  isAllSelected,
  onCollection,
  onPayment 
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedBookingList = [...bookingList]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'hireDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getIndicator= (status) => {
    const statusLabel = statusOptions.find(option => option.value === status).label;

    const statusConfig = {
      'Completed': { color: 'active' },
      'In Progress': { color: 'bg-yellow-900' },
      'Pending': { color: 'bg-slate-700' },
      'Cancelled': { color: 'bg-red-900' }
    };
    
    return <div className={`status-indicator mr-1 ${statusConfig?.[statusLabel].color || statusConfig?.Pending.color}`}></div>
  };

  const getStatusBadge = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);

    const statusConfig = {
      Completed: { color: 'bg-green-700 text-success-foreground', label: 'Completed' },
      'In Progress': { color: 'bg-yellow-500 text-success-foreground', label: 'In Progress' },
      Pending: { color: 'bg-muted text-muted-foreground', label: 'Pending' },
      Cancelled: { color: 'bg-red-500 text-warning-foreground', label: 'Cancelled' }
    };

    const config = statusConfig?.[statusOption.label] || statusConfig?.Pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {getIndicator(status)}
        {config?.label}
      </span>
    );
  };

  const getAmountStatusBadge = (paid, amount) => {
    const statusConfig = {
      Paid: { color: 'bg-success text-success-foreground', label: 'Completed' },
      Pending: { color: 'bg-muted text-muted-foreground', label: 'Pending' },
    };
    
    const config = paid? statusConfig.Paid : statusConfig.Pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <div className={`status-indicator mr-1 ${paid ? 'active' : 'warning'}`}></div>
        R{amount}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'Manager': 'bg-primary text-primary-foreground',
      'Supervisor': 'bg-accent text-accent-foreground',
      'Washer': 'bg-secondary text-secondary-foreground',
      'Cashier': 'bg-muted text-muted-foreground'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${roleColors?.[role] || 'bg-muted text-muted-foreground'}`}>
        {role}
      </span>
    );
  };

  const SortButton = ({ field, children }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className="h-auto p-1 font-medium text-left justify-start hover:font-semibold hover:bg-transparent hover:text-blue-800"
    >
      {children}
      <Icon 
        name={sortField === field ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
        size={14} 
        className="ml-1"
      />
    </Button>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={onSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4">
                <SortButton field="name">Customer Name</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="email">Contact</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="role">Vehicle Details</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="role">Amount Due</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="role">Services</SortButton>
              </th>

              <th className="text-left p-4">
                <SortButton field="status">Status</SortButton>
              </th>
              
              <th className="text-left p-4">
                <SortButton field="hireDate">Date</SortButton>
              </th>
              
              <th className="text-center p-4 w-32 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBookingList?.map((booking) => (
              <tr key={booking?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedBooking?.includes(booking?.id)}
                    onChange={() => onSelectBooking(booking?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    {/* <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={staff?.avatar}
                        alt={staff?.name}
                        className="w-full h-full object-cover"
                      />
                    </div> */}
                    <div>
                      <div className="text-sm text-foreground">{booking?.name} {booking?.surname}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <div className="text-sm text-foreground">{booking?.emailAddress}</div>
                    <div className="text-sm text-muted-foreground">{booking?.contactNumber}</div>
                  </div>
                </td>
                <td className="p-4 text-sm">
                  <div className="text-sm text-foreground">{booking?.bookedItemDetails.colour+' '+booking?.bookedItemDetails.name+' '+booking?.bookedItemDetails.model || 'N/A'}</div>
                  <div className="text-sm text-muted-foreground italic">{booking?.bookedItemDetails.registrationNo }</div>
                </td>
                <td className="p-4 text-sm">
                  {getAmountStatusBadge(booking.totalAmountSettled,booking.totalAmountDue)}
                </td>
                <td className="p-4 text-sm">
                  {booking?.services.map(service=>(
                    <div key={service.id} className='text-xs text-black flex flex-row items-center'>
                      <Icon name="Check" size={16} className="mr-1 text-green-900" /> {service?.serviceName}
                    </div>
                    ))
                  }
                  {/* <Button
                    variant="link"
                    size="sm"
                    onClick={() => onCollection(booking)}
                    className="px-3 py-1 rounded-md"
                  >
                    <Icon name="Cog" size={16} className="mr-1" />
                    View Services
                  </Button> */}
                </td>
                
                
                <td className="p-4">
                  {getStatusBadge(booking?.status)}
                </td>
                <td className="p-4">
                  <div className="text-sm text-foreground">
                    {new Date(booking.bookingDate)?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </td>
                <td className="p-4">
                  {!booking.collected?
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewDetails(booking)}
                      className="h-8 w-8"
                      title="Update Job"
                    >
                      <Icon name="NotepadText" size={16} />
                    </Button>
                    {(booking.totalAmountSettled || booking.totalAmountDue == 0)?
                    <>
                       <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onCollection(booking)}
                        className="h-8 w-8"
                        disabled={!(booking.status===3 || booking.status===2)}
                        title={(booking.status===3 || booking.status===2) ? "Collection" : "Colllection Disabled until job is completed or cancelled"}
                      >
                        <Icon name="ShieldCheck" size={16} />
                      </Button>
                      
                    </>
                    :
                    <>
                      {/* <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(booking)}
                        className="h-8 w-8"
                        title="Edit Booking"
                      >
                        <Icon name="Edit" size={16} />
                      </Button> */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onPayment(booking)}
                        className={`h-8 w-8`}
                        title="Make Payment"
                      >
                        <Icon name='CreditCard' size={16} />
                      </Button>
                    </>
                    }
                    
                  </div>:
                  <div className='flex justify-center  items-center flex-row py-1 px-2 rounded-full bg-gray-400x space-x-1'> 
                    <Icon name="Info" size={16} className='text-cyan-700'/>
                    <span className='text-xs font-semibold text-cyan-900'>Collected</span>
                  </div>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedBookingList?.map((booking) => (
          <div key={booking?.id} className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 flex-1">
                {/* <input
                  type="checkbox"
                  checked={selectedBooking?.includes(booking?.id)}
                  onChange={() => onSelectBooking(booking?.id)}
                  className="rounded border-border mt-1"
                /> */}
                {/* <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={staff?.avatar}
                    alt={staff?.name}
                    className="w-full h-full object-cover"
                  />
                </div> */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">{booking?.name} {booking?.surname}</div>
                  <div className="text-sm text-muted-foreground flex flex-row items-center">
                    <Icon name="Phone" size={14} className="mr-1" />
                    {booking?.contactNumber}
                  </div>
                  <div className="text-sm text-muted-foreground flex flex-row items-center">
                    <Icon name="Mail" size={14} className="mr-1" />
                    {booking?.emailAddress}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <div>
                  {getAmountStatusBadge(booking.totalAmountSettled,booking.totalAmountDue)}
                </div>
                {/* <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onViewDetails(booking)}
                  className="h-8 w-8"
                >
                  <Icon name="Eye" size={16} />
                </Button> */}
                {/* <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(booking)}
                  className="h-8 w-8"
                >
                  <Icon name="Edit" size={16} />
                </Button> */}
              </div>
            </div>

            <div className='text-sm'>
              <div className="text-muted-foreground">Vehicle</div>
              <div className="text-foreground truncate">{booking?.bookedItemDetails.colour} {booking?.bookedItemDetails.name}</div>
            </div>
            <div className='text-sm'>
              <div className="text-muted-foreground">Vehicle Model & Registration</div>
              <div className="text-foreground">{booking?.bookedItemDetails.model}, {booking?.bookedItemDetails.registrationNo}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              
              <div>
                <div className="text-muted-foreground">Booking Date</div>
                <div className="text-foreground">
                  {new Date(booking.bookingDate)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div className="mt-1">{getStatusBadge(booking?.status)}</div>
              </div>
            </div>
            <div className='text-sm'>
              <div className='text-muted-foreground'>Services</div>
              <div className='grid grid-cols-2 gap-1'>
                {booking?.services?.map((service) => (
                  <div key={service.id} className='text-xs text-black flex flex-row items-center'>
                    <Icon name="Check" size={16} className="mr-1 text-green-900" /> {service?.serviceName}
                  </div>
                ))}
              </div>
              
              {booking?.notes && (
                <>
                  <div className='text-muted-foreground'>Notes</div>
                  <div className='text-xs'>{booking?.notes}</div>
                </>
              )}
            </div>
            {booking.collected && (
              <div className='flex justify-center  items-center flex-row py-1 px-2 rounded-full bg-gray-400x space-x-1'> 
                <Icon name="Info" size={16} className='text-cyan-700'/>
                <span className='text-xs font-semibold text-cyan-900'>Collected</span>
              </div>
            )}

            {!booking.collected && 
              <div className="flex justify-end pt-2 border-t border-border gap-2">
                {!booking.totalAmountSettled?
                (
                  <Button
                    variant='success'
                    size="sm"
                    onClick={() => onPayment(booking)}
                    iconName='CreditCard'
                    iconPosition="left"
                    iconSize={16}
                  >
                    Payment
                  </Button>
                ):(
                  <Button
                    variant='success'
                    size="sm"
                    onClick={() => onCollection(booking)}
                    iconName='ShieldCheck'
                    iconPosition="left"
                    iconSize={16}
                  >
                    Collection
                  </Button>
                )}
                <Button
                  variant={booking?.status === 'Completed' ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => onViewDetails(booking)}
                  iconName={'NotepadText'}
                  iconPosition="left"
                  iconSize={16}
                >
                  Update
                </Button>
              </div>
            }
          </div>
        ))}
      </div>
      {/* Empty State */}
      {bookingList?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Bookings</h3>
          <p className="text-muted-foreground mb-4">Get started by adding a first booking.</p>
        </div>
      )}
    </div>
  );
};

export default BookingsTable;